/**
 * Preset Phase 1 (2026-05-20): preset-aware default maps.
 *
 * Replaces hardcoded physician/IR fallbacks throughout the frontend with
 * preset-driven defaults so non-physician tenants see profession-appropriate
 * defaults out of the box.
 *
 * Referenced by:
 *   - src/pages/Hours.tsx (work-type default + dropdown ordering + server-value fallback)
 *   - src/components/settings/CompensationCard.tsx (rate-card label fallback)
 *   - src/pages/Onboarding.tsx (specialty-capture step placeholder copy)
 *
 * Source of truth: per the Preset Phase 1 PRE-FIX audit
 * (docs/audits/PRE-FIX-AUDIT-2026-05-20-preset-phase-1-generalization.md).
 *
 * When adding a new preset:
 *   1. Add it to `OnboardingPreset` in src/types/index.ts
 *   2. Add it to `ONBOARDING_PRESETS` in src/lib/onboarding-presets.ts with
 *      a sensible defaultDailyRate or defaultHourlyRate
 *   3. Add it to every map below (TypeScript Record<OnboardingPreset, T> will
 *      error at compile if you forget — that's the regression guard)
 */

import type { OnboardingPreset } from '@/types';
import type { TimeEntryWorkType } from '@/lib/api';

/**
 * The default work_type value to use when the server returns an unknown
 * value OR the form is initialized fresh OR the user clears the field.
 *
 * Pre-Phase-1 hardcoded fallback was 'clinical_shift' (physician-only).
 * Phase 1 makes it preset-aware. Aalpen (physician_ir) still gets
 * 'clinical_shift'; a CPA gets 'billable_hour'; a realtor gets
 * 'commission_event'; etc.
 */
export const WORK_TYPE_FALLBACK_PER_PRESET: Record<OnboardingPreset, TimeEntryWorkType> = {
  physician_ir:       'clinical_shift',
  physician_other:    'clinical_shift',
  // NS13: NP locums bill clinical shifts/days like physicians.
  nurse_practitioner: 'clinical_shift',
  dentist:            'production_day',
  attorney:           'billable_hour',
  cpa:                'billable_hour',
  engineer:           'project_work',
  architect:          'project_work',
  realtor:            'commission_event',
  real_estate:        'per_unit',
  consultant:         'consulting',
  freelancer:         'billable_hour',
  // NS13: RIA owner logs planning hours (AUM fees are entity revenue, not time-tracked).
  financial_advisor:  'billable_hour',
  custom:             'other',
};

/**
 * Specialty-capture placeholder copy for the onboarding step that asks
 * "What's your specialty / focus?". Each preset gets a list of plausible
 * examples to help the user fill in the free-text field.
 *
 * Used by Onboarding.tsx — Phase 2a (P2a, 2026-05-21) keeps this map as
 * the help-line under the <select>, since the dropdown options live in
 * SPECIALTY_OPTIONS_PER_PRESET below. The "Other" option in the dropdown
 * reveals a free-text fallback so custom values stay possible.
 */
export const SPECIALTY_PLACEHOLDER_PER_PRESET: Record<OnboardingPreset, string> = {
  physician_ir:       'E.g., Interventional Radiology (default), Diagnostic Radiology, Vascular',
  physician_other:    'E.g., Hospitalist, ER, Family Medicine, Surgery, Anesthesia, Psychiatry',
  nurse_practitioner: 'E.g., Family (FNP), Acute Care (ACNP), Psychiatric (PMHNP), Emergency, Hospitalist',
  dentist:            'E.g., General, Oral Surgery (OMS), Orthodontics, Periodontics, Endodontics, Pediatric',
  attorney:           'E.g., Litigation, M&A, Intellectual Property, Family Law, Tax',
  cpa:                'E.g., Tax, Audit, Advisory, Fractional CFO, Bookkeeping',
  engineer:           'E.g., Civil, Structural, Mechanical, Electrical, Environmental',
  architect:          'E.g., Residential, Commercial, Healthcare, Education, Sustainable',
  realtor:            'E.g., Residential, Commercial, Land, Luxury, Property Management',
  real_estate:        'E.g., Long-term residential rentals, Short-term rentals, Commercial, Flips',
  consultant:         'E.g., Strategy, Operations, Technology, Marketing, Finance',
  freelancer:         'E.g., Web development, Design, Writing, Photography, Video',
  financial_advisor:  'E.g., Wealth management, Financial planning, Retirement, Fee-only RIA',
  custom:             'Your specialty or focus area',
};

/**
 * Preset Phase 2a (P2a, 2026-05-21): preset-conditional specialty dropdown
 * options. Replaces the free-text-only specialty field on /onboarding step 4.
 *
 * The list mirrors the examples in SPECIALTY_PLACEHOLDER_PER_PRESET above
 * — keeping the two in sync is a maintenance pattern; the dropdown is what
 * users see, the placeholder is the help-line. When dropdown options change,
 * audit the placeholder for parallel updates.
 *
 * `custom` preset returns an empty list — the UI keeps the free-text input
 * unconditionally for custom (no profession known, no curated list possible).
 *
 * Every dropdown also renders an "Other" option at the end (in UI, not here)
 * which reveals a fallback free-text input. Backward compat: any existing
 * entities.specialty value (free-text VARCHAR 100) stays valid; no enum.
 *
 * Source of truth for option text: PRESET-PHASE-1 audit. P2a expansion
 * locks these as the dropdown contract; subsequent additions require
 * BOTH a SPECIALTY_OPTIONS entry AND a placeholder update in lockstep.
 */
export const SPECIALTY_OPTIONS_PER_PRESET: Record<OnboardingPreset, readonly string[]> = {
  physician_ir:       ['Interventional Radiology', 'Diagnostic Radiology', 'Vascular'],
  physician_other:    ['Hospitalist', 'ER', 'Family Medicine', 'Surgery', 'Anesthesia', 'Psychiatry'],
  nurse_practitioner: ['Family (FNP)', 'Acute Care (ACNP)', 'Psychiatric (PMHNP)', 'Emergency', 'Hospitalist', 'Adult-Gerontology'],
  dentist:            ['General', 'Oral Surgery (OMS)', 'Orthodontics', 'Periodontics', 'Endodontics', 'Pediatric'],
  attorney:           ['Litigation', 'M&A', 'Intellectual Property', 'Family Law', 'Tax'],
  cpa:                ['Tax', 'Audit', 'Advisory', 'Fractional CFO', 'Bookkeeping'],
  engineer:           ['Civil', 'Structural', 'Mechanical', 'Electrical', 'Environmental'],
  architect:          ['Residential', 'Commercial', 'Healthcare', 'Education', 'Sustainable'],
  realtor:            ['Residential', 'Commercial', 'Land', 'Luxury', 'Property Management'],
  real_estate:        ['Long-term residential rentals', 'Short-term rentals', 'Commercial', 'Flips'],
  consultant:         ['Strategy', 'Operations', 'Technology', 'Marketing', 'Finance'],
  freelancer:         ['Web development', 'Design', 'Writing', 'Photography', 'Video'],
  financial_advisor:  ['Wealth management', 'Financial planning', 'Retirement planning', 'Fee-only RIA', 'Tax-focused'],
  custom:             [],
};

/**
 * UI sentinel value for the "Other (type your own)" option in the specialty
 * dropdown. Exported so the test matrix and the page logic share the
 * same string. Not user-facing label — the label is "Other".
 */
export const SPECIALTY_OTHER_SENTINEL = '__other__';

/**
 * Primary work-type label per preset — used for the auto-seeded rate card
 * label on entity creation. The supervisor's auto-seed logic looks this up
 * after entity create.
 */
export const PRIMARY_RATE_CARD_LABEL_PER_PRESET: Record<OnboardingPreset, string> = {
  physician_ir:       'Weekday call',
  physician_other:    'Standard shift',
  nurse_practitioner: 'Standard shift',
  dentist:            'Production day',
  attorney:           'Standard hourly',
  cpa:                'Standard hourly',
  engineer:           'Standard hourly',
  architect:          'Standard hourly',
  realtor:            'Commission (per closing)',  // not auto-seeded — realtor is commission-based
  real_estate:        'Per-property base',          // not auto-seeded — REI is per-unit
  consultant:         'Day rate',
  freelancer:         'Standard hourly',
  financial_advisor:  'Planning hour',
  custom:             'Standard',
};

/**
 * Generic / preset-neutral rate-card copy used when CompensationCard's
 * preset lookup fails (e.g., unknown preset, or tenant onboarded before
 * preset selection existed). Pre-Phase-1 this fallback was PHYSICIAN_PRESET
 * which leaked physician terminology to non-physician tenants. Phase 1
 * replaces with this neutral default.
 */
export const GENERIC_PRESET_COPY = {
  primaryLabel: 'Standard',
  alternateLabel: 'Premium',
  retainerLabel: 'Retainer',
  projectLabel: 'Project',
  placeholder: '1000',
} as const;

/**
 * Home operating model per preset (Home Redesign, 2026-06-02).
 *
 * Drives WHICH center block the Company home renders — the schedule-spine
 * (paid_for_work) vs the A/R collections view (billing). The USER never picks
 * this; the app derives it from the entity's onboarding preset.
 *
 *  - paid_for_work: paid per day / shift / encounter, no client invoicing
 *    (locums physicians, NPs, dentists on production days). Home center =
 *    "your schedule -> expected revenue".
 *  - billing: sends invoices / collects A/R (attorneys, CPAs, engineers,
 *    consultants, freelancers, advisors, realtors, REI rent-receivable).
 *    Home center = A/R aging / collections.
 *
 * `custom` and any unknown preset default to 'billing' (the more universal
 * money-in center). Per PRE-FIX-AUDIT-2026-06-02-home-redesign this is a pure
 * TS lookup — NO `entities.operating_model` column is required for v1.
 */
export type OperatingModel = 'paid_for_work' | 'billing';

export const OPERATING_MODEL_PER_PRESET: Record<OnboardingPreset, OperatingModel> = {
  physician_ir:       'paid_for_work',
  physician_other:    'paid_for_work',
  nurse_practitioner: 'paid_for_work',
  dentist:            'paid_for_work',
  attorney:           'billing',
  cpa:                'billing',
  engineer:           'billing',
  architect:          'billing',
  realtor:            'billing',
  real_estate:        'billing',
  consultant:         'billing',
  freelancer:         'billing',
  financial_advisor:  'billing',
  custom:             'billing',
};

/**
 * Resolve the home operating model for an entity from its (possibly unknown)
 * preset string. Pure + total: any legacy/empty/unrecognized value defaults
 * to 'billing'. Safe to call with anything.
 */
export function resolveOperatingModel(preset: string | null | undefined): OperatingModel {
  if (preset && Object.prototype.hasOwnProperty.call(OPERATING_MODEL_PER_PRESET, preset)) {
    return OPERATING_MODEL_PER_PRESET[preset as OnboardingPreset];
  }
  return 'billing';
}
